import * as React from 'react';
import { BlockSkeleton, FilterAnchor, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { MIDDLEGAME_TURNS, MIDDLEGAME_TURN_LABEL } from '@chess/api-model';
import type { MiddlegameTurnsResult, TurnBand } from './MiddlegameTurnsAggregator.ts';
import { MIDDLEGAME_TURNS_HELP } from './help.tsx';
import './middlegame-turns.css';

// When it turns: over the last 30 days, the score the reader's own
// middlegame moves gave up, as a column per band of move numbers (up to 15,
// five moves each, 36 on), so the player sees whether they drift early or
// late. A column's height is the pawns given up per middlegame that played
// moves in the band (that count is in its tooltip), so early and late bands read
// on one scale; it opens the games that gave up half a pawn or more there
// (`middlegame-turn`), as many as its tooltip says. The "?" opens the help
// topic. Drawn from props only: the card's body, which the section draws in
// the dashboard's frame (`MIDDLEGAME_TURNS_CARD`, `registerCard`).

export type MiddlegameTurnsCardProps = CardProps<{ result: MiddlegameTurnsResult }, {
  /** Opens the library filtered to a column's games. */
  link?: FilterLink;
}>;

/** The frame's title and help topic: the registration's and the stories'. */
export const MIDDLEGAME_TURNS_CARD = { title: 'When it turns', help: MIDDLEGAME_TURNS_HELP };
const ASIDE = 'score lost by move number';
const NOTE = (n: number) => `pawns per middlegame · ${middlegames(n)}`;

const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;
const middlegames = (n: number) => `${n} ${n === 1 ? 'middlegame' : 'middlegames'}`;
/** Pawns given up per middlegame, two decimals, with the minus: "−1.84". */
const lost = (pawns: number) => (pawns > 0 ? `−${pawns.toFixed(2)}` : '0');

/** The loading columns' heights, as shares of the track: a plausible shape. */
const SAMPLE = [55, 80, 65, 40, 25, 15];

const Columns: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="turns-body">
    <div className="turns-chart" style={{ '--turns-columns': MIDDLEGAME_TURNS.length } as React.CSSProperties}>
      {children}
    </div>
    <div className="turns-axis">move</div>
  </div>
);

const Column: React.FC<{ band: TurnBand; max: number; link?: FilterLink }> = ({ band, max, link }) => {
  const label = MIDDLEGAME_TURN_LABEL[band.band];
  const title = !band.played
    ? `Moves ${label}: no middlegame played them`
    : `Moves ${label}: ${lost(band.pawns)} pawns per middlegame over ${middlegames(band.played)} · ` +
      (band.turned ? `${games(band.turned)} gave up half a pawn or more · opens them` : 'none gave up half a pawn');
  return (
    <FilterAnchor className="turns-col" link={band.turned ? link : undefined} filter={{ middlegameTurn: band.band }} title={title}>
      <span className={band.pawns > 0 ? 'turns-value' : 'turns-value turns-value-none'}>{band.played ? lost(band.pawns) : '–'}</span>
      <span className="turns-track">
        {band.pawns > 0 && <span className="turns-bar" style={{ height: `${(100 * band.pawns) / max}%` }} />}
      </span>
      <span className="turns-label">{label}</span>
    </FilterAnchor>
  );
};

export const MiddlegameTurnsCard: React.FC<MiddlegameTurnsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <Columns>
          {MIDDLEGAME_TURNS.map((band, i) => (
            <span className="turns-col" key={band}>
              <span className="turns-value">
                <TextSkeleton sample="−0.00" />
              </span>
              <span className="turns-track">
                <BlockSkeleton height={`${SAMPLE[i % SAMPLE.length]}%`} />
              </span>
              <span className="turns-label">{MIDDLEGAME_TURN_LABEL[band]}</span>
            </span>
          ))}
        </Columns>
        <CardNote>
          <TextSkeleton sample={NOTE(24)} />
        </CardNote>
      </>
    );
  const { result, link } = props;
  if (!result.middlegames)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No scanned middlegames in the last 30 days.</CardEmpty>
      </>
    );
  const max = Math.max(0.1, ...result.bands.map((b) => b.pawns));
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <Columns>
        {result.bands.map((b) => (
          <Column key={b.band} band={b} max={max} link={link} />
        ))}
      </Columns>
      <CardNote>{NOTE(result.middlegames)}</CardNote>
    </>
  );
};
